import React, { useState } from 'react';
import { Wallet, ChevronDown, LogOut, Copy } from 'lucide-react';
import { useWallet } from '../context/WalletContext';
import { shortAddr } from '../lib/wallet';
import { networkOf } from '../lib/networks';
import { copyToClipboard } from '../lib/clipboard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { toast } from 'sonner';

const ConnectWalletButton = () => {
  const { wallet, connect, disconnect, connecting } = useWallet();
  const [open, setOpen] = useState(false);

  const handleConnect = async (kind) => {
    try {
      await connect(kind);
      setOpen(false);
      toast.success('Wallet connected');
    } catch (e) {
      toast.error(e?.message || 'Connection failed');
    }
  };

  if (wallet?.address) {
    const net = networkOf(wallet.chainId);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="wallet-menu-trigger" className="cf-btn-secondary text-sm">
            <span className="hidden sm:inline cf-chip !py-0 !px-2">{net.short}</span>
            <span className="font-code">{shortAddr(wallet.address)}</span>
            <ChevronDown className="w-4 h-4 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-[#0B0A2A]/95 backdrop-blur-xl border border-white/10 rounded-2xl text-sm min-w-[200px]">
          <DropdownMenuItem
            data-testid="wallet-copy-address"
            className="cursor-pointer rounded-xl focus:bg-white/10"
            onSelect={(e) => {
              // Defer the clipboard write until AFTER the dropdown starts closing —
              // otherwise Radix's focus-trap tear-down can race with navigator.clipboard
              // and cancel the write silently.
              e.preventDefault();
              setTimeout(() => { copyToClipboard(wallet.address, 'Address'); }, 0);
            }}>
            <Copy className="w-4 h-4 mr-2" /> Copy address
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem
            data-testid="wallet-disconnect"
            className="cursor-pointer rounded-xl text-[#FF6B7A] focus:bg-[#FF6B7A]/10"
            onClick={() => { disconnect(); toast.success('Disconnected'); }}>
            <LogOut className="w-4 h-4 mr-2" /> Disconnect
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="connect-wallet-btn" className="cf-btn-primary">
          <Wallet className="w-4 h-4" /> Connect Wallet
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B0A2A]/95 backdrop-blur-2xl border border-white/10 rounded-3xl text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">Choose your wallet</DialogTitle>
          <p className="text-sm text-forge-muted mt-1">Sign the deployment with your own keys — CoinForge never sees them.</p>
        </DialogHeader>
        <div className="space-y-3 pt-3">
          <button
            data-testid="connect-metamask"
            disabled={connecting}
            onClick={() => handleConnect('metamask')}
            className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#7DF9FF]/40 transition-all group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-amber-600 flex items-center justify-center font-display font-bold text-white">M</div>
              <span className="font-display font-medium">MetaMask</span>
            </div>
            <span className="text-forge-muted group-hover:text-[#7DF9FF] transition-colors">→</span>
          </button>
          <button
            data-testid="connect-coinbase"
            disabled={connecting}
            onClick={() => handleConnect('coinbase')}
            className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#7DF9FF]/40 transition-all group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center font-display font-bold text-white">C</div>
              <span className="font-display font-medium">Coinbase Wallet</span>
            </div>
            <span className="text-forge-muted group-hover:text-[#7DF9FF] transition-colors">→</span>
          </button>
          <p className="text-xs text-forge-muted pt-2 leading-relaxed">
            Opens the extension installed in your browser. You sign the deployment there — CoinForge never sees your keys.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectWalletButton;
